import { Button, FloatButton, Input, Modal, Radio } from 'antd';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';
import { submitFeedbackMutation } from '@tools/shared';
import { IconType, IconWrap, semanticTheme, Typography } from '@tools/ui/dime';
import { SubmitFeedbackResponse } from '@tools/ui/types/generated/graphql';
import useNotifyActions from '@tools/ui-hooks/useNotifyAction.hook';
import useT from '@tools/ui-hooks/useT';

const FEEDBACK_PREFIX = 'shared.feedback';

const FeedbackText = styled(Typography)`
  white-space: nowrap;
  overflow: hidden;
  opacity: 0;
  max-width: 0px;
  margin-left: 0;
  color: ${semanticTheme.dime.colors.text.inverse};
  transition: opacity 0.2s ease-in-out 0.1s, max-width 0.2s ease-in-out 0.1s,
    margin-left 0.2s ease-in-out 0.1s;
`;

const FeedbackButtonContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${semanticTheme.dime.spacing.m};
  height: 100%;
  width: 100%;
  color: white;
`;

const StyledFloatButton = styled(FloatButton)`
  width: 45px;
  height: 45px;
  right: -5px;
  bottom: 16px;
  padding: ${semanticTheme.dime.spacing.s};
  background: ${semanticTheme.dime.colors.action.primary} !important;
  border-radius: ${semanticTheme.dime.spacing.m};
  box-shadow: 0px 2px 16px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: width 0.3s ease-in-out, padding 0.3s ease-in-out,
    justify-content 0.3s ease-in-out;
  opacity: 0.9;

  .ant-float-btn-body {
    background: transparent !important;
    display: flex !important;
    align-items: center !important;
    justify-content: inherit;
    width: 100%;
    height: 100%;
    padding: 0;
    overflow: hidden;
  }

  &:hover {
    width: 135px;
    padding: ${semanticTheme.dime.spacing.m} ${semanticTheme.dime.spacing.l};
    justify-content: flex-start;
    opacity: 1;

    ${FeedbackText} {
      opacity: 1;
      max-width: 80px;
      margin-left: ${semanticTheme.dime.spacing.s};
    }
  }
`;

const FeedbackModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${semanticTheme.dime.spacing.xl};
  padding-top: ${semanticTheme.dime.spacing.l};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${semanticTheme.dime.spacing.m};
`;

const SearchRowContainer = styled.div`
  width: 100%;
  && .ant-radio-group {
    width: 100%;
    display: flex;
    .ant-radio-button-wrapper {
      flex: 1 1 0;
      text-align: center;
      font-size: 14px;
      &:hover {
        color: ${semanticTheme.dime.colors.action.primaryHover};
      }
    }
    .ant-radio-button-wrapper-checked {
      background-color: ${semanticTheme.dime.colors.action.primary};
      border-color: ${semanticTheme.dime.colors.action.primary};
      &:hover {
        background-color: ${semanticTheme.dime.colors.action.primaryHover};
        border-color: ${semanticTheme.dime.colors.action.primaryHover};
        color: ${semanticTheme.dime.colors.action.positiveSecondaryHover};
      }
    }
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${semanticTheme.dime.spacing.l};
`;

enum Subject {
  feedback = 'feedback',
  bugReport = 'bugReport',
  featureRequest = 'featureRequest'
}

const Feedback: React.FC<
  { slackChannelId: string } & React.PropsWithChildren
> = ({ slackChannelId }) => {
  const { t } = useT();

  const SUBJECT_MAP = {
    [Subject.feedback]: {
      value: Subject.feedback,
      label: t(`${FEEDBACK_PREFIX}.subjectOptions.feedback`)
    },
    [Subject.bugReport]: {
      value: Subject.bugReport,
      label: t(`${FEEDBACK_PREFIX}.subjectOptions.bugReport`)
    },
    [Subject.featureRequest]: {
      value: Subject.featureRequest,
      label: t(`${FEEDBACK_PREFIX}.subjectOptions.featureRequest`)
    }
  };

  const defaultSubject = SUBJECT_MAP.feedback.value;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState('');

  const closeModal = () => {
    setIsModalOpen(false);
    setSubject(defaultSubject);
    setMessage('');
  };

  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noreferrer');
  };

  const [submitFeedback, { loading }] = useMutation<{
    submitFeedback: SubmitFeedbackResponse;
  }>(submitFeedbackMutation);

  const { onCompleted, onError } = useNotifyActions({
    successMsg: `${FEEDBACK_PREFIX}.notifications.success`,
    errorMsg: `${FEEDBACK_PREFIX}.notifications.failure`,
    onCompleteCb: closeModal,
    placement: 'bottomRight'
  });

  const handleSubmit = () => {
    submitFeedback({
      variables: {
        slackChannelId,
        type: SUBJECT_MAP[subject].label,
        message,
        page: window.location.href
      },
      onCompleted: (data) => {
        onCompleted();
        if (data.submitFeedback.viewFeedback) {
          openInNewTab(data.submitFeedback.viewFeedback);
        }
      },
      onError
    });
  };

  return (
    <>
      <StyledFloatButton
        description={
          <FeedbackButtonContent>
            <IconWrap type={IconType.ChatNotificationHuge} />
            <FeedbackText variant="heading4">
              {t(`${FEEDBACK_PREFIX}.cta`)}
            </FeedbackText>
          </FeedbackButtonContent>
        }
        shape="square"
        onClick={() => setIsModalOpen(true)}
      />
      <Modal
        open={isModalOpen}
        title={
          <Typography variant="heading3Bold">
            {t(`${FEEDBACK_PREFIX}.title`)}
          </Typography>
        }
        onCancel={closeModal}
        footer={
          <ModalFooter>
            <Button disabled={loading} onClick={closeModal}>
              {t(`${FEEDBACK_PREFIX}.cancel`)}
            </Button>
            <Button
              loading={loading}
              type="primary"
              onClick={handleSubmit}
              disabled={!message.trim()}
            >
              {t(`${FEEDBACK_PREFIX}.submit`)}
            </Button>
          </ModalFooter>
        }
        width={500}
        centered
        destroyOnClose
      >
        <FeedbackModalContent>
          <FieldGroup>
            <Typography variant="body1Bold">
              {t(`${FEEDBACK_PREFIX}.subject`)}
            </Typography>
            <SearchRowContainer>
              <Radio.Group
                options={Object.values(SUBJECT_MAP)}
                value={subject}
                optionType="button"
                buttonStyle="solid"
                onChange={(e) => setSubject(e.target.value)}
                size="large"
              />
            </SearchRowContainer>
          </FieldGroup>
          <FieldGroup>
            <Typography variant="body1Bold">
              {t(`${FEEDBACK_PREFIX}.message`)}
            </Typography>
            <Input.TextArea
              placeholder={t(`${FEEDBACK_PREFIX}.placeholder`)}
              value={message}
              rows={4}
              onChange={(e) => setMessage(e.target.value)}
            />
          </FieldGroup>
        </FeedbackModalContent>
      </Modal>
    </>
  );
};

export default Feedback;
